import { create } from 'zustand';

export type DiffNavigationSource = 'hash' | 'programmatic';

export type DiffNavigationContext = {
  filename?: string;
  source?: DiffNavigationSource;
  targetHeadSha?: string;
  expandFile?: boolean;
  scrollToDraft?: boolean;
  suppressScroll?: boolean;
};

export type DiffNavigationHandlers = {
  getViewportAnchorFilename: () => string | null;
  navigateToFile: (filename: string, context?: DiffNavigationContext) => boolean;
  navigateToGroup: (groupId: string) => boolean;
  navigateToComment: (commentId: string, context?: DiffNavigationContext) => boolean;
  setFilesViewed: (filenames: string[], viewed: boolean) => boolean;
};

type DiffNavigationStore = {
  handlers: Partial<DiffNavigationHandlers>;
  setHandlers: (handlers: Partial<DiffNavigationHandlers>) => void;
  clearHandlers: () => void;
  getViewportAnchorFilename: DiffNavigationHandlers['getViewportAnchorFilename'];
  navigateToFile: (filename: string, context?: DiffNavigationContext) => boolean;
  navigateToGroup: (groupId: string) => boolean;
  navigateToComment: (commentId: string, context?: DiffNavigationContext) => boolean;
  setFilesViewed: DiffNavigationHandlers['setFilesViewed'];
};

export const useDiffNavigationStore = create<DiffNavigationStore>((set, get) => ({
  handlers: {},
  setHandlers: (handlers) => set({ handlers }),
  clearHandlers: () => set({ handlers: {} }),
  getViewportAnchorFilename: () => get().handlers.getViewportAnchorFilename?.() ?? null,
  navigateToFile: (filename, context) =>
    get().handlers.navigateToFile?.(filename, context) ?? false,
  navigateToGroup: (groupId) => get().handlers.navigateToGroup?.(groupId) ?? false,
  navigateToComment: (commentId, context) =>
    get().handlers.navigateToComment?.(commentId, context) ?? false,
  setFilesViewed: (filenames, viewed) =>
    get().handlers.setFilesViewed?.(filenames, viewed) ?? false,
}));
